// Service-Worker-Registrierung für die PWA.
//
// Wichtig: vite-plugin-pwa registriert den SW NICHT automatisch obwohl
// `injectRegister: 'auto'` konfiguriert ist — das würde nur passieren wenn
// der Build-Output inline im HTML landet. Wir registrieren explizit via
// `virtual:pwa-register` damit die Workbox-Runtime-Caches (pmtiles-v2,
// trails-v1, api-manifest-v1) tatsächlich aktiv werden.
//
// Ohne diese Registrierung:
//   - PMTiles wurden zwar über CacheStorage direkt gecached (downloadToCache),
//     aber Workbox's rangeRequests-Plugin lief nie → bei schwachem Netz fiel
//     die App auf Network zurück und brach ab.
//   - /api/trails und /api/tiles/manifest hatten keinen Cache → Flicker/Fehler
//     bei jedem kurzen Verbindungsabbruch.

import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;

// Wave 14dz: Update-Check beim App-Resume.
//
// Problem (User-Report nach dem Orange-Deploy: "die App öffnet immer noch
// mit grüner Kompass-Rose"): auf iOS wird eine Homescreen-PWA beim Schliessen
// nicht beendet, sondern SUSPENDIERT. Beim nächsten Öffnen läuft dieselbe
// Seite mit demselben JS-Bundle weiter — es gibt keinen Page-Load, also auch
// keinen registerSW()-Aufruf und keinen SW-Update-Check. Der setInterval
// unten hilft nicht: Timer sind im suspendierten Zustand eingefroren und
// feuern beim Resume nicht nach. Ergebnis: der User kann tagelang auf einem
// alten Bundle sitzen, obwohl der Deploy längst durch ist.
//
// Fix: bei jedem Sichtbar-Werden (visibilitychange), bei bfcache-Restores
// (pageshow) und beim Wiederkommen des Netzes einen update() feuern. Findet
// der Browser ein neues sw.js, greift skipWaiting + clientsClaim, das
// 'activated'-Event von virtual:pwa-register (registerType 'autoUpdate')
// macht den Reload — die neue Version ist dann sofort da.
let _registration: ServiceWorkerRegistration | null = null;
let _lastUpdateCheck = 0;
let _resumeHooksInstalled = false;

/** Mindestabstand zwischen zwei Update-Checks — schützt vor Tab-Switch-Spam. */
const UPDATE_CHECK_MIN_GAP_MS = 60 * 1000;

function checkForUpdate(reason: string): void {
  if (!_registration) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const now = Date.now();
  if (now - _lastUpdateCheck < UPDATE_CHECK_MIN_GAP_MS) return;
  _lastUpdateCheck = now;
  _registration.update().catch((err) => {
    console.warn(`[sw] update-Check (${reason}) fehlgeschlagen:`, err);
  });
}

function installResumeHooks(): void {
  if (_resumeHooksInstalled) return;
  _resumeHooksInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate('resume');
  });
  // pageshow feuert auch bei bfcache-Restores, wo visibilitychange ausbleibt.
  window.addEventListener('pageshow', () => checkForUpdate('pageshow'));
  // Nach einem Funkloch: sobald wieder Netz da ist, einmal nachfassen.
  window.addEventListener('online', () => checkForUpdate('online'));
}

/**
 * Wave 14aw: SW-Status exportieren, damit die UI (OfflinePage) anzeigen kann
 * ob der SW überhaupt aktiv ist. Ohne aktiven SW werden PMTiles-Range-
 * Requests nicht abgefangen → offline = leere Karte, obwohl der Cache
 * physisch existiert. Dieses Silent-Failure-Szenario hat uns auf iOS
 * wochenlang Kopfweh bereitet; jetzt sehen wir es sofort in der UI.
 *
 * Werte:
 *   'unknown'     — Initialzustand, Registrierung läuft noch
 *   'active'      — SW ist erfolgreich registriert und aktiv
 *   'failed'      — Registrierung fehlgeschlagen (Fehler im Catch oder
 *                   onRegisterError)
 *   'unsupported' — Browser ohne ServiceWorker-API (Safari Private Mode)
 */
export type ServiceWorkerState = 'unknown' | 'active' | 'failed' | 'unsupported';

let _swState: ServiceWorkerState = 'unknown';
const _swStateListeners = new Set<(s: ServiceWorkerState) => void>();

function setSwState(s: ServiceWorkerState) {
  if (_swState === s) return;
  _swState = s;
  for (const l of _swStateListeners) l(s);
}

export function getServiceWorkerState(): ServiceWorkerState {
  return _swState;
}

/** React-Hook: abonniert den aktuellen SW-State. Re-rendert bei Änderung. */
export function useServiceWorkerState(): ServiceWorkerState {
  const [s, setS] = useState<ServiceWorkerState>(_swState);
  useEffect(() => {
    // Sofort synchron aktuellen Wert übernehmen — falls der SW sich zwischen
    // Mount und useEffect-Lauf schon registriert hat.
    setS(_swState);
    const listener = (next: ServiceWorkerState) => setS(next);
    _swStateListeners.add(listener);
    return () => {
      _swStateListeners.delete(listener);
    };
  }, []);
  return s;
}

/**
 * Registriert den Service Worker. Nur in Production aktiv
 * (devOptions.enabled: false in vite.config.ts).
 *
 * Ruft sich nicht selbst wieder auf — idempotent.
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) {
    console.info('[sw] Service Worker nicht unterstützt (vermutlich Safari Private Mode).');
    setSwState('unsupported');
    return;
  }
  if (updateSW) return; // schon registriert

  try {
    updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // Neues Bundle verfügbar — beim nächsten sanften Reload ziehen.
        // Kein aufdringliches Banner: die App ist offline-first, ein Refresh
        // passiert sowieso wenn der User die Karte neu öffnet.
        console.info('[sw] Neues App-Update verfügbar, wird beim nächsten Start aktiv.');
      },
      onOfflineReady() {
        console.info('[sw] App ist offline bereit.');
        setSwState('active');
      },
      onRegisteredSW(swUrl, registration) {
        console.info('[sw] registriert:', swUrl);
        setSwState('active');
        // Periodisch nach Updates checken — aber nur wenn online.
        // 1h Intervall damit es nicht die Batterie frisst. Läuft NUR solange
        // die App im Vordergrund ist; für den iOS-Resume-Fall siehe
        // installResumeHooks() oben.
        if (registration) {
          _registration = registration;
          _lastUpdateCheck = Date.now();
          installResumeHooks();
          setInterval(() => checkForUpdate('interval'), 60 * 60 * 1000);
        }
      },
      onRegisterError(err) {
        console.error('[sw] Registrierung fehlgeschlagen:', err);
        setSwState('failed');
      },
    });
  } catch (err) {
    // In Dev (devOptions.enabled: false) wirft das virtual:pwa-register
    // ein No-Op — kein Grund die App crashen zu lassen. Den State lassen
    // wir auf 'unknown' damit die UI nicht fälschlich "failed" zeigt.
    console.info('[sw] Registrierung übersprungen (Dev-Modus?):', err);
  }
}
